import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Box, 
  CircularProgress,
  Typography,
  Divider
} from '@mui/material';
import { debounce } from 'lodash';
import { useGetAllCountries } from '../../api/country';
import { useGetAllCities } from '../../api/city';

// Define types to match your API expectations
interface City {
  id: number;
  libelle: string;
}

interface Country {
  id: number;
  libelle: string;
}

interface CitySelectProps {
  label: string;
  placeholder: string;
  marginBottom?: string;
  marginTop?: string;
  value: string | number;
  onChange: (value: any) => void;
  initialCityData?: { id: string | number; libelle: string } | null;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

export const CitySelect: React.FC<CitySelectProps> = ({
  label,
  marginBottom = '0px',
  marginTop = '0px',
  value,
  onChange,
  initialCityData = null,
  size = 'medium',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Stable debounced search function
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setPage(1);
      setAllCities([]);
      setSearchTerm(term);
    }, 300),
    []
  );

  // Fetch cities with current page and search
  const { 
    cities, 
    pagination, 
    isLoading, 
    isFetching 
  } = useGetAllCities({
    page,
    per_page: 50, // Increased to reduce API calls
    search: searchTerm,
  });

  // Initialize with existing city data if provided
  useEffect(() => {
    if (initialCityData && !isInitialized) {
      // Convert initialCityData to match City type
      const cityData: City = {
        id: typeof initialCityData.id === 'string' ? parseInt(initialCityData.id, 10) : initialCityData.id,
        libelle: initialCityData.libelle
      };
      setAllCities([cityData]);
      setIsInitialized(true);
    }
  }, [initialCityData, isInitialized]);

  // Update cities list when new data arrives
  useEffect(() => {
    if (cities) {
      if (page === 1) {
        // Merge with initial data if exists
        const newCities = [...cities];
        if (initialCityData && !newCities.find(city => String(city.id) === String(initialCityData.id))) {
          const cityData: City = {
            id: typeof initialCityData.id === 'string' ? parseInt(initialCityData.id, 10) : initialCityData.id,
            libelle: initialCityData.libelle
          };
          newCities.unshift(cityData);
        }
        setAllCities(newCities);
      } else {
        setAllCities(prev => {
          const existingIds = new Set(prev.map(city => String(city.id)));
          const newCities = cities.filter(city => !existingIds.has(String(city.id)));
          return [...prev, ...newCities];
        });
      }
      
      setHasMore(pagination ? pagination.current_page < pagination.last_page : false);
    }
  }, [cities, page, pagination, initialCityData]);

  // Handle scroll for pagination
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    
    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      hasMore && 
      !isFetching
    ) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, isFetching]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    debouncedSearch(term);
  }, [debouncedSearch]);

  // Handle city selection
  const handleCitySelect = useCallback((cityId: string | number) => {
    const selectedCity = allCities.find(city => String(city.id) === String(cityId));
    if (selectedCity) {
      onChange({
        id: selectedCity.id,
        libelle: selectedCity.libelle
      });
    }
  }, [allCities, onChange]);

  // Clean up debounced function
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Build menu items array
  const menuItems = useMemo(() => {
    const items = [];
    
    // Search field
    items.push(
      <MenuItem 
        key="search"
        disableRipple 
        sx={{ 
          '&:hover': { backgroundColor: 'transparent' },
          '&:focus': { backgroundColor: 'transparent' },
          cursor: 'default',
          paddingTop: 1,
          paddingBottom: 1
        }}
      >
        <TextField
          size="small"
          placeholder="Search city..."
          fullWidth
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          autoFocus={false}
        />
      </MenuItem>
    );

    // Divider
    items.push(<Divider key="divider" />);

    // Loading state
    if (isLoading && page === 1 && allCities.length === 0) {
      items.push(
        <MenuItem key="loading" disabled>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading...</Typography>
          </Box>
        </MenuItem>
      );
    } else {
      // City items
      allCities.forEach((city) => {
        items.push(
          <MenuItem key={`city-${city.id}`} value={String(city.id)}>
            {city.libelle}
          </MenuItem>
        );
      });
      
      // Loading more indicator
      if (isFetching && page > 1) {
        items.push(
          <MenuItem key="loading-more" disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading more...</Typography>
            </Box>
          </MenuItem>
        );
      }
      
      // No results found
      if (allCities.length === 0 && !isLoading) {
        items.push(
          <MenuItem key="no-results" disabled>
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </MenuItem>
        );
      }
    }

    return items;
  }, [isLoading, page, allCities, isFetching, handleSearchChange]);

  return (
    <Box sx={{ marginBottom, marginTop }}>
      <FormControl fullWidth size={size}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value ? String(value) : ''}
          onChange={(e) => handleCitySelect(e.target.value)}
          label={label}
          disabled={disabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
              onScroll: handleScroll,
            },
            autoFocus: false,
          }}
        >
          {menuItems}
        </Select>
      </FormControl>
    </Box>
  );
};

// Enhanced CountrySelect Component
interface CountrySelectProps {
  label: string;
  placeholder: string;
  marginBottom?: string;
  marginTop?: string;
  value: string | number;
  onChange: (value: any) => void;
  initialCountryData?: { id: string | number; libelle: string } | null;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  label,
  marginBottom = '0px',
  marginTop = '0px',
  value,
  onChange,
  initialCountryData = null,
  size = 'medium',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Stable debounced search function
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setPage(1);
      setAllCountries([]);
      setSearchTerm(term);
    }, 300),
    []
  );

  // Fetch countries with current page and search
  const { 
    countries, 
    pagination, 
    isLoading, 
    isFetching 
  } = useGetAllCountries({
    page,
    per_page: 50, // Increased to reduce API calls
    search: searchTerm,
  });

  // Initialize with existing country data if provided
  useEffect(() => {
    if (initialCountryData && !isInitialized) {
      // Convert initialCountryData to match Country type
      const countryData: Country = {
        id: typeof initialCountryData.id === 'string' ? parseInt(initialCountryData.id, 10) : initialCountryData.id,
        libelle: initialCountryData.libelle
      };
      setAllCountries([countryData]);
      setIsInitialized(true);
    }
  }, [initialCountryData, isInitialized]);

  // Update countries list when new data arrives
  useEffect(() => {
    if (countries) {
      if (page === 1) {
        // Merge with initial data if exists
        const newCountries = [...countries];
        if (initialCountryData && !newCountries.find(country => String(country.id) === String(initialCountryData.id))) {
          const countryData: Country = {
            id: typeof initialCountryData.id === 'string' ? parseInt(initialCountryData.id, 10) : initialCountryData.id,
            libelle: initialCountryData.libelle
          };
          newCountries.unshift(countryData);
        }
        setAllCountries(newCountries);
      } else {
        setAllCountries(prev => {
          const existingIds = new Set(prev.map(country => String(country.id)));
          const newCountries = countries.filter(country => !existingIds.has(String(country.id)));
          return [...prev, ...newCountries];
        });
      }
      
      setHasMore(pagination ? pagination.current_page < pagination.last_page : false);
    }
  }, [countries, page, pagination, initialCountryData]);

  // Handle scroll for pagination
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    
    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      hasMore && 
      !isFetching
    ) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, isFetching]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    debouncedSearch(term);
  }, [debouncedSearch]);

  // Handle country selection
  const handleCountrySelect = useCallback((countryId: string | number) => {
    const selectedCountry = allCountries.find(country => String(country.id) === String(countryId));
    if (selectedCountry) {
      onChange({
        id: selectedCountry.id,
        libelle: selectedCountry.libelle
      });
    }
  }, [allCountries, onChange]);

  // Clean up debounced function
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Build menu items array
  const menuItems = useMemo(() => {
    const items = [];
    
    // Search field
    items.push(
      <MenuItem 
        key="search"
        disableRipple 
        sx={{ 
          '&:hover': { backgroundColor: 'transparent' },
          '&:focus': { backgroundColor: 'transparent' },
          cursor: 'default',
          paddingTop: 1,
          paddingBottom: 1
        }}
      >
        <TextField
          size="small"
          placeholder="Search country..."
          fullWidth
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          autoFocus={false}
        />
      </MenuItem>
    );

    // Divider
    items.push(<Divider key="divider" />);

    // Loading state
    if (isLoading && page === 1 && allCountries.length === 0) {
      items.push(
        <MenuItem key="loading" disabled>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading...</Typography>
          </Box>
        </MenuItem>
      );
    } else {
      // Country items
      allCountries.forEach((country) => {
        items.push(
          <MenuItem key={`country-${country.id}`} value={String(country.id)}>
            {country.libelle}
          </MenuItem>
        );
      });
      
      // Loading more indicator
      if (isFetching && page > 1) {
        items.push(
          <MenuItem key="loading-more" disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading more...</Typography>
            </Box>
          </MenuItem>
        );
      }
      
      // No results found
      if (allCountries.length === 0 && !isLoading) {
        items.push(
          <MenuItem key="no-results" disabled>
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </MenuItem>
        );
      }
    }

    return items;
  }, [isLoading, page, allCountries, isFetching, handleSearchChange]);

  return (
    <Box sx={{ marginBottom, marginTop }}>
      <FormControl fullWidth size={size}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value ? String(value) : ''}
          onChange={(e) => handleCountrySelect(e.target.value)}
          label={label}
          disabled={disabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
              onScroll: handleScroll,
            },
            autoFocus: false,
          }}
        >
          {menuItems}
        </Select>
      </FormControl>
    </Box>
  );
};